/**
 * BLRRD Selection — model.js  (Phase 4.B.select.1)
 *
 * Single Float32Array(w*h) mask, value in [0,1]. The data structure is
 * intentionally minimal: w, h, mask, plus a monotonically-increasing
 * `version` counter that overlay caches (marching ants Path2D, listeners,
 * etc.) bump on each mutation. A nullable `contour` slot lets the overlay
 * module cache a marching-squares Path2D and invalidate via version
 * comparison.
 *
 * CRITICAL invariant — null-mask = "everything selected"
 * ──────────────────────────────────────────────────────
 * `selection.mask === null` MUST mean "everything selected". The render
 * kernel skips the blend entirely in that case so:
 *
 *   1. The pixel-hash + worker-parity baselines stay byte-identical to
 *      pre-Wave-8 — no per-pixel multiply, no allocation.
 *   2. Default editor state (image just loaded, no selection drawn yet)
 *      has zero cost.
 *   3. Deselect = mask back to null = back to zero-cost path.
 *
 * Never replace `mask = null` with an allocated all-1.0 Float32Array as
 * a "default state" — that bypass is what protects the baselines.
 *
 * Provenance / clean-room note
 * ────────────────────────────
 * BLRRD is MIT. This module is implemented from public algorithm
 * references and standard graphics texts. No GPL/LGPL source code (GIMP,
 * GEGL, RawTherapee, darktable) was inspected, ported, or copied. The
 * conceptual model (single 8-bit-equivalent channel mask) is described
 * in any standard computer-graphics text (Foley/van Dam etc.) and the
 * Photoshop / GIMP user-facing docs.
 */

/**
 * Allocate a fresh selection object.
 *
 * @param {number} w  image width in pixels
 * @param {number} h  image height in pixels
 * @returns {{version: number, mask: Float32Array | null, w: number, h: number, contour: Path2D | null}}
 */
export function createSelection(w, h) {
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
        throw new Error(`createSelection: invalid dims (${w} × ${h})`);
    }
    return {
        version: 0,
        mask: null,          /* null === everything selected (see top-of-file) */
        w: w | 0,
        h: h | 0,
        contour: null,       /* cached Path2D rebuilt on version bump */
    };
}

/**
 * @param {object} sel
 * @returns {boolean} true when no mask is allocated (full image selected)
 */
export function isAllSelected(sel) {
    return !sel || sel.mask === null;
}

/**
 * Ensure `sel.mask` is allocated. Idempotent — if a mask already exists
 * this is a no-op. Otherwise allocates Float32Array(w*h) filled with
 * 1.0 (everything selected, just materialised). The transition does NOT
 * bump `version`: callers that follow up with a rasteriser (rect,
 * ellipse, magic wand, etc.) bump version themselves on the actual
 * geometric change, so the overlay only invalidates once.
 *
 * @param {object} sel
 * @returns {Float32Array} the (now-allocated) mask
 */
export function ensureMask(sel) {
    if (!sel) throw new Error('ensureMask: selection is null');
    if (sel.mask === null) {
        const n = sel.w * sel.h;
        const m = new Float32Array(n);
        m.fill(1);
        sel.mask = m;
    }
    return sel.mask;
}

/**
 * Drop the mask and bump version. Restores the "everything selected"
 * (null-mask) invariant.
 *
 * @param {object} sel
 */
export function clearSelection(sel) {
    if (!sel) return;
    sel.mask = null;
    sel.contour = null;
    sel.version = (sel.version | 0) + 1;
}

/**
 * Invert the active mask. If the mask is null (everything selected),
 * the inverse is "nothing selected" — allocate a zero-filled mask.
 * Otherwise compute `out[i] = 1 - mask[i]` in place.
 *
 * @param {object} sel
 */
export function invertSelection(sel) {
    if (!sel) return;
    if (sel.mask === null) {
        const m = new Float32Array(sel.w * sel.h);
        /* fill with 0 (zeroed by default) — "nothing selected" */
        sel.mask = m;
    } else {
        const m = sel.mask;
        for (let i = 0; i < m.length; i++) m[i] = 1 - m[i];
    }
    sel.contour = null;
    sel.version = (sel.version | 0) + 1;
}

/**
 * Bump the version counter without changing the mask. Callers that
 * mutate the mask directly (e.g. through rasterizers) MUST call this
 * after their mutation so overlay caches invalidate.
 *
 * @param {object} sel
 */
export function bumpVersion(sel) {
    if (!sel) return;
    sel.contour = null;
    sel.version = (sel.version | 0) + 1;
}

/**
 * Serialize a selection for snapshot persistence. Returns a JSON-safe
 * object: { w, h, mask: null | base64 } where `mask` is base64-encoded
 * Float32 bytes (LE). Mirrors the pixel-layer serialization shape.
 *
 * @param {object} sel
 * @returns {object | null}
 */
export function serializeSelection(sel) {
    if (!sel) return null;
    const out = { w: sel.w, h: sel.h, mask: null };
    if (sel.mask === null) return out;
    /* Base64-encode the underlying Float32 bytes. */
    const u8 = new Uint8Array(sel.mask.buffer, sel.mask.byteOffset, sel.mask.byteLength);
    out.mask = u8ToBase64(u8);
    return out;
}

/**
 * Restore a selection previously written by `serializeSelection`. If
 * `payload.mask === null` returns a brand-new null-mask selection at
 * the recorded dims. Throws on shape mismatch.
 *
 * @param {object | null} payload
 * @returns {object | null}
 */
export function deserializeSelection(payload) {
    if (!payload) return null;
    const w = payload.w | 0, h = payload.h | 0;
    if (w < 1 || h < 1) return null;
    const sel = createSelection(w, h);
    if (payload.mask) {
        const u8 = base64ToU8(payload.mask);
        const expected = w * h * 4;
        if (u8.byteLength !== expected) {
            throw new Error(`deserializeSelection: byte mismatch (got ${u8.byteLength}, want ${expected})`);
        }
        sel.mask = new Float32Array(u8.buffer, u8.byteOffset, w * h);
        sel.version = (sel.version | 0) + 1;
    }
    return sel;
}

/* ─── Base64 helpers (browser + node compat) ───────────────────────── */
function u8ToBase64(u8) {
    /* Browser path. */
    if (typeof btoa === 'function') {
        let s = '';
        const CHUNK = 0x8000;
        for (let i = 0; i < u8.length; i += CHUNK) {
            s += String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK));
        }
        return btoa(s);
    }
    /* Node fallback. */
    return Buffer.from(u8).toString('base64');
}
function base64ToU8(b64) {
    if (typeof atob === 'function') {
        const bin = atob(b64);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xFF;
        return out;
    }
    return new Uint8Array(Buffer.from(b64, 'base64'));
}
